import { useEffect, useState } from "react"
import { useLocalization } from "@fluent/react"
import { Box, LinearProgress, Stack, Typography } from "@mui/material"
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined"
import { alpha, useTheme } from "@mui/material/styles"
import { MinuteMeetingStatus } from "../../project/mettingMinutes/meetingMinutesSlice"

interface NewMMTimerProps {
  startDate: string
  plannedMinutes: number
  status: MinuteMeetingStatus
  compact?: boolean

  // Data zakończenia lub zawieszenia, jeśli timer nie jest aktywny.
  stoppedAt?: string | null
}

const formatDuration = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  const pad = (value: number) => String(value).padStart(2, "0")

  return hours > 0
    ? `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
    : `${pad(minutes)}:${pad(seconds)}`
}

export const NewMMTimer = ({
  startDate,
  plannedMinutes,
  status,
  stoppedAt,
  compact = false,
}: NewMMTimerProps) => {
  const theme = useTheme()
  const { l10n } = useLocalization()
  const [now, setNow] = useState(() => Date.now())

  const isRunning = status === "INMEETING"

  useEffect(() => {
    if (!isRunning) return

    setNow(Date.now())

    const interval = window.setInterval(() => {
      setNow(Date.now())
    }, 1000)

    return () => window.clearInterval(interval)
  }, [isRunning, startDate])

  const startMs = Date.parse(startDate)
  const endMs = isRunning
    ? now
    : stoppedAt
      ? Date.parse(stoppedAt)
      : NaN

  const hasTime =
    Number.isFinite(startMs) &&
    Number.isFinite(endMs) &&
    endMs >= startMs

  const elapsedSeconds = hasTime
    ? Math.floor((endMs - startMs) / 1000)
    : 0

  const hasPlan = Number.isFinite(plannedMinutes) && plannedMinutes > 0

  const plannedSeconds = hasPlan
    ? Math.max(1, Math.round(plannedMinutes * 60))
    : 0

  const remainingSeconds = plannedSeconds - elapsedSeconds

  const progress = hasPlan
    ? Math.min(100, (elapsedSeconds / plannedSeconds) * 100)
    : 0

  const isOvertime = hasTime && hasPlan && remainingSeconds < 0
  const isNearEnd = hasTime && hasPlan && progress >= 80

  const accentColor = isOvertime
    ? theme.palette.error.main
    : isNearEnd
      ? theme.palette.warning.main
      : theme.palette.primary.main

  const timeDescription = !hasTime
    ? "—"
    : !hasPlan
      ? l10n.getString("meeting-timer-no-limit", null, "_Bez limitu czasu")
      : isOvertime
        ? l10n.getString("meeting-timer-overtime", { time: formatDuration(-remainingSeconds) }, `_Przekroczono o ${formatDuration(-remainingSeconds)}`)
        : remainingSeconds === 0
          ? l10n.getString("meeting-timer-time-up", null, "_Planowany czas upłynął")
          : isRunning
            ? l10n.getString("meeting-timer-remaining", { time: formatDuration(remainingSeconds) }, `_Pozostało ${formatDuration(remainingSeconds)}`)
            : l10n.getString("meeting-timer-spare", { time: formatDuration(remainingSeconds) }, `_Zapas do planu: ${formatDuration(remainingSeconds)}`)

  if (compact) {
    return (
      <Stack direction="row" alignItems="baseline" spacing={1} sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
        <Typography component="span" variant="body2" fontWeight={600}
          aria-label={l10n.getString("meeting-refresh-time", null, "_Czas spotkania")}
          color={isOvertime ? "error.main" : "text.primary"}>
          {hasTime ? formatDuration(elapsedSeconds) : "—"}
        </Typography>
        <Typography component="span" variant="caption" color={isOvertime ? "error.main" : "text.secondary"}>
          {timeDescription}
        </Typography>
      </Stack>
    )
  }

  return (
    <Stack spacing={0.5} sx={{ minWidth: 0, width: "100%" }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        useFlexGap
        gap={1}
      >
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: "text.secondary" }}>
          <ScheduleOutlinedIcon sx={{ fontSize: 16 }} />
          <Typography variant="caption">
            {l10n.getString("meeting-refresh-time", null, "_Czas spotkania")}
          </Typography>
        </Stack>
        <Typography
          component="span"
          sx={{
            fontSize: 22,
            fontWeight: 600,
            lineHeight: 1.2,
            fontVariantNumeric: "tabular-nums",
            whiteSpace: "nowrap",
            color: isOvertime ? "error.main" : "text.primary",
          }}
        >
          {hasTime ? formatDuration(elapsedSeconds) : "—"}
        </Typography>

      </Stack>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        useFlexGap
        flexWrap="wrap"
        columnGap={1.5}
        rowGap={0.25}
      >
        <Typography
          variant="caption"
          sx={{
            color: isOvertime ? "error.main" : "text.secondary",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {timeDescription}
        </Typography>

        {hasPlan && (
          <Typography variant="caption" color="text.secondary">
            {l10n.getString("meeting-timer-plan", { minutes: plannedMinutes }, `_Plan: ${plannedMinutes} min`)}
          </Typography>
        )}
      </Stack>

      {hasPlan && hasTime && (
        <Box sx={{ pt: 0.25 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            aria-label={l10n.getString("meeting-timer-progress", null, "_Wykorzystany czas spotkania")}
            sx={{
              height: 4,
              borderRadius: 2,
              bgcolor: alpha(accentColor, 0.12),
              "& .MuiLinearProgress-bar": {
                borderRadius: 2,
                bgcolor: accentColor,
              },
            }}
          />
        </Box>
      )}
    </Stack>
  )
}